import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, CheckCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { imgUrl } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";

export default function CartDrawer() {
  const { cart, cartTotal, cartOpen, setCartOpen, setQty, removeFromCart } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="bg-[#FAF9F6] w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer" aria-describedby="cart-drawer-desc">
        <span id="cart-drawer-desc" className="sr-only">Items in your shopping bag with quantity controls and checkout options</span>
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/10">
          <SheetTitle className="font-serif text-2xl flex items-center gap-3">
            <CheckCircle strokeWidth={1.5} size={22} className="text-gold" />
            Added to Your Bag
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {cart.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-16">Your bag is empty.</p>
          ) : (
            <div className="flex flex-col gap-5">
              {cart.map((item) => {
                const price = item.product.sale_price || item.product.price;
                return (
                  <div key={item.product.id} className="flex gap-4" data-testid={`drawer-item-${item.product.slug}`}>
                    <Link to={`/product/${item.product.slug}`} onClick={() => setCartOpen(false)}>
                      <img src={imgUrl(item.product.images?.[0])} alt={item.product.name} className="w-20 h-20 object-cover border border-black/10" />
                    </Link>
                    <div className="flex-1">
                      <p className="font-serif text-sm leading-snug">{item.product.name}</p>
                      <p className="text-sm font-medium mt-1">₹{price.toLocaleString("en-IN")}</p>
                      <div className="flex items-center border border-black/15 w-max mt-2">
                        <button onClick={() => setQty(item.product.id, item.qty - 1)} aria-label="Decrease quantity" className="p-1.5 hover:text-gold transition-colors duration-300" data-testid={`drawer-qty-minus-${item.product.slug}`}>
                          <Minus strokeWidth={1.5} size={13} />
                        </button>
                        <span className="px-3 text-sm">{item.qty}</span>
                        <button onClick={() => setQty(item.product.id, item.qty + 1)} aria-label="Increase quantity" className="p-1.5 hover:text-gold transition-colors duration-300" data-testid={`drawer-qty-plus-${item.product.slug}`}>
                          <Plus strokeWidth={1.5} size={13} />
                        </button>
                      </div>
                    </div>
                    <button onClick={() => removeFromCart(item.product.id)} aria-label="Remove item" className="self-start text-muted-foreground hover:text-destructive transition-colors duration-300" data-testid={`drawer-remove-${item.product.slug}`}>
                      <Trash2 strokeWidth={1.5} size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-black/10 px-6 py-5 bg-white">
            <div className="flex justify-between mb-4">
              <span className="text-sm text-muted-foreground">Subtotal</span>
              <span className="font-semibold" data-testid="drawer-subtotal">₹{cartTotal.toLocaleString("en-IN")}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setCartOpen(false)}
                className="border border-black/20 py-3.5 small-caps hover:border-gold hover:text-gold transition-colors duration-300"
                data-testid="drawer-continue">
                Continue Shopping
              </button>
              <button onClick={() => { setCartOpen(false); navigate("/cart"); }}
                className="bg-[#111] text-[#FAF9F6] py-3.5 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300"
                data-testid="drawer-view-bag">
                View Bag
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
